import { emitKeyDown, emitKeyUp } from './events/piano-events';

const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

function midiNoteToName(midi: number): string {
  const octave = Math.floor(midi / 12) - 1;
  return `${NOTE_NAMES[midi % 12]}${octave}`;
}

export type MidiStatus = 'pending' | 'connected' | 'no-devices' | 'unsupported' | 'denied';

export function initMidi(onStatus: (status: MidiStatus) => void): () => void {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const nav = navigator as any;
  if (!nav.requestMIDIAccess) {
    onStatus('unsupported');
    return () => {};
  }

  const activeNotes = new Set<number>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let midiAccess: any = null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onMessage = (event: any) => {
    const data = event.data as Uint8Array;
    const type = data[0] & 0xf0;
    const note = data[1];
    const velocity = data[2];
    const name = midiNoteToName(note);

    if (type === 0x90 && velocity > 0) {
      if (!activeNotes.has(note)) {
        activeNotes.add(note);
        emitKeyDown(name);
      }
    } else if (type === 0x80 || (type === 0x90 && velocity === 0)) {
      if (activeNotes.has(note)) {
        activeNotes.delete(note);
        emitKeyUp(name);
      }
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const setup = (access: any) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    access.inputs.forEach((input: any) => {
      input.onmidimessage = onMessage;
    });
    onStatus(access.inputs.size > 0 ? 'connected' : 'no-devices');
  };

  const releaseAll = () => {
    activeNotes.forEach(n => emitKeyUp(midiNoteToName(n)));
    activeNotes.clear();
  };

  nav.requestMIDIAccess().then(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (access: any) => {
      midiAccess = access;
      setup(access);
      access.onstatechange = () => {
        releaseAll();
        setup(access);
      };
    },
    () => {
      onStatus('denied');
    },
  );

  return () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (midiAccess) midiAccess.inputs.forEach((input: any) => { input.onmidimessage = null; });
    releaseAll();
  };
}
